import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  optionsSelector,
  articleSelector,
  articlesSelector,
  totalSelector,
  pageSelector,
  noMoreSelector,
  classifiedArticlesSelector,
} from './store/selector';
import { GetArticleParams } from './interfaces/get-articles';
import { Article } from '../../interfaces/article.interface';
import { PutArticlePayloadRequest } from './interfaces/put-article';
import { articlesApiService } from './services/api';
import {
  getArticlesInitApiAction,
  getArticlesSuccessApiAction,
  getArticlesFailureApiAction,
  postArticleInitApiAction,
  postArticleFailuireApiAction,
  putArticleInitApiAction,
  putArticleFailuireApiAction,
  deleteArticleInitApiAction,
  deleteArticleFailuireApiAction,
  setArticleAction,
  setOptionsAction,
  resetOptionsAction,
  increasePageAction,
  articleClearAction,
  postArticleSuccessApiAction,
  deleteArticleSuccessApiAction,
  putArticleSuccessApiAction,
  getClassifiedArticlesSuccessApiAction,
  getClassifiedArticlesFailureApiAction,
  getClassifiedArticlesInitApiAction,
  articlesClearAction,
  postArticleFileFailuireApiAction,
  postArticleFileSuccessApiAction,
  postArticleFileInitApiAction,
  deleteFileInitApiAction,
  deleteFileSuccessApiAction,
  deleteFileFailuireApiAction,
} from './store/reducer';
import { PostArticlePayloadRequest } from './interfaces/post-article';
import { GetClassifiedArticleParams } from './interfaces/get-classified-articles';

export function useArticlesCore() {
  const articles = useSelector(articlesSelector);
  const classifiedArticles = useSelector(classifiedArticlesSelector);
  const article = useSelector(articleSelector);
  const total = useSelector(totalSelector);
  const page = useSelector(pageSelector);
  const noMore = useSelector(noMoreSelector);
  const options = useSelector(optionsSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getArticles = useCallback(
    async (
      id: number,
      params: GetArticleParams,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getArticlesInitApiAction());
        const response = await articlesApiService.getArticles(id, params);
        dispatch(getArticlesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getArticlesFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getClassifiedArticles = useCallback(
    async (
      id: number,
      params: GetClassifiedArticleParams,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getClassifiedArticlesInitApiAction());
        const response = await articlesApiService.getClassifiedArticles(id, params);
        dispatch(getClassifiedArticlesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getClassifiedArticlesFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getArticle = useCallback(
    async (
      id: number,
      params: GetArticleParams,
      onSuccess?: (article: Article) => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getArticlesInitApiAction());
        const response = await articlesApiService.getArticle(id, params);
        dispatch(getArticlesSuccessApiAction(response));
        onSuccess?.(response.products[0]);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getArticlesFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postArticle = useCallback(
    async (
      id: number,
      body: PostArticlePayloadRequest,
      onSuccess?: (id: number, code: string) => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postArticleInitApiAction());
        const response = await articlesApiService.postArticle(id, body);
        dispatch(postArticleSuccessApiAction());
        onSuccess?.(response.id, response.code  );
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postArticleFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postArticleFile = useCallback(
    async (
      cId: number,
      pId: number,
      file: File,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postArticleFileInitApiAction());
        await articlesApiService.postArticleFile(cId, pId, file);
        dispatch(postArticleFileSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postArticleFileFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const putArticle = useCallback(
    async (
      cId: number,
      pId: number,
      body: PutArticlePayloadRequest,
      onSuccess?: (id: number) => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putArticleInitApiAction());
        await articlesApiService.putArticle(cId, pId, body);
        dispatch(putArticleSuccessApiAction());
        onSuccess?.(pId);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          putArticleFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const deleteArticle = useCallback(
    async (cId: number, pId: number, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(deleteArticleInitApiAction());
        await articlesApiService.deleteArticle(cId, pId);
        dispatch(deleteArticleSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(deleteArticleFailuireApiAction());
      }
    },
    [dispatch]
  );

  const deleteFile = useCallback(
    async (cId: number, pId: number, fId: number, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(deleteFileInitApiAction());
        await articlesApiService.deleteFile(cId, pId, fId);
        dispatch(deleteFileSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(deleteFileFailuireApiAction());
      }
    },
    [dispatch]
  );

  const setArticle = useCallback(
    (article: Article) => {
      dispatch(setArticleAction(article));
    },
    [dispatch]
  );

  const setOptions = useCallback(
    (options: GetArticleParams) => {
      dispatch(setOptionsAction(options));
    },
    [dispatch]
  );

  const resetOptions = useCallback(() => {
    dispatch(resetOptionsAction());
  }, [dispatch]);

  const increasePage = useCallback(() => {
    dispatch(increasePageAction());
  }, [dispatch]);

  const clearArticle = useCallback(() => {
    dispatch(articleClearAction());
  }, [dispatch]);

  const clearArticles = useCallback(() => {
    dispatch(articlesClearAction());
  }, [dispatch]);

  const clearError = useCallback(() => {
    dispatch(getArticlesInitApiAction());
  }, [dispatch]);

  return {
    articles,
    classifiedArticles,
    article,
    options,
    total,
    page,
    noMore,
    errorCode,
    getArticles,
    getClassifiedArticles,
    getArticle,
    postArticle,
    postArticleFile,
    putArticle,
    deleteArticle,
    deleteFile,
    setArticle,
    setOptions,
    increasePage,
    resetOptions,
    clearArticle,
    clearArticles,
    clearError,
  };
}
